import { Fragment, type MouseEvent } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn.js';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onSelect?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  /** Tracking .2em (default) or .25em for page-header eyebrows. */
  tracking?: '0.2em' | '0.25em';
  className?: string;
}

/** Mono 10px uppercase breadcrumbs separated by ChevronRight 11 (DESIGN_SYSTEM section 7.2). Leaf is --ink. */
export function Breadcrumbs({ items, tracking = '0.2em', className }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-1.5 font-mono text-2xs uppercase text-mute" style={{ letterSpacing: tracking }}>
        {items.map((item, i) => {
          const last = i === items.length - 1;
          const interactive = !last && (item.href || item.onSelect);
          return (
            <Fragment key={`${item.label}-${i}`}>
              {i > 0 ? <ChevronRight size={11} aria-hidden className="shrink-0 text-dim" /> : null}
              <li className={cn('min-w-0 truncate', last ? 'text-ink' : '')} aria-current={last ? 'page' : undefined}>
                {interactive ? (
                  item.href ? (
                    <a href={item.href} onClick={item.onSelect} className="rounded-sm transition-colors hover:text-ink">
                      {item.label}
                    </a>
                  ) : (
                    <button type="button" onClick={item.onSelect} className="rounded-sm uppercase transition-colors hover:text-ink">
                      {item.label}
                    </button>
                  )
                ) : (
                  item.label
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
